import { TDatasetQueryParams } from '@infotech-soe/merps/types/Queries';
import {
  CategoryFieldName,
  TDatabaseId,
  TDatasetName,
} from '@infotech-soe/metaschema';
import { createSelector } from '@reduxjs/toolkit';

import { CategoryOpenMode } from '../../models/category/category';
import { DatasetGridViewModel } from '../../models/dataset/DatasetRelated';
import { DocLinksMode } from '../../models/docLinks/docLinks';
import {
  DatasetConfig,
  IFrontDatasetPreset,
  TDatasetTotalsValues,
  TRecordViewMap,
} from '../../models/user-config/DisplayModeConfig';
import DatasetViewModel from '../../services/category/view-model/DatasetViewModel';
import { CommandsNameKeyMap } from '../../services/commands/constants';
import { DatasetGridColumn } from '../../services/dataset/view-model/DatasetGridColumn';
import { DatasetGridRow } from '../../services/dataset/view-model/DatasetGridRow';
import { PredefinedForms } from '../../services/form/view-model/FormCreateViewModel';
import { FormSourceType } from '../../services/form/view-model/FormSource';
import UniqueId from '../../services/uid/UniqueId';
import { AppState } from '../reducers/root';
import { computeSelectedTotals } from '../utils/datasetTotals';

import {
  getAllChildrenDatasetsFromCategory,
  getCategoryMasterDataset,
  getCategoryState,
} from './category';
import { getFormState } from './form';

export const getDatasetState = (
  state: AppState,
  datasetId: UniqueId
): DatasetGridViewModel => state.dataset[datasetId];

export const isDatasetOpen = (state: AppState, datasetId: UniqueId): boolean =>
  Boolean(getDatasetState(state, datasetId));

export const getDatasetColumns = (
  state: AppState,
  datasetId: UniqueId
): DatasetGridColumn[] => getDatasetState(state, datasetId).columns;

export const getDatasetTotalsValues = (
  state: AppState,
  datasetId: UniqueId
): TDatasetTotalsValues | undefined => getDatasetState(state, datasetId).totals;

export const getDatasetRows = (
  state: AppState,
  datasetId: UniqueId
): DatasetGridRow[] => getDatasetState(state, datasetId).rows;

export const getDatasetRowsFullCount = (
  state: AppState,
  datasetId: UniqueId
): number => getDatasetState(state, datasetId).rowsFullCount;

export const getDatasetCategoryId = (
  state: AppState,
  datasetId: UniqueId
): UniqueId => getDatasetState(state, datasetId).categoryId;

export const getDatasetCategoryOpenMode = (
  state: AppState,
  datasetId: UniqueId
): CategoryOpenMode =>
  state.category[getDatasetCategoryId(state, datasetId)].openMode;

export const getDatasetRowIdField = (
  state: AppState,
  datasetId: UniqueId
): CategoryFieldName => getDatasetState(state, datasetId).rowIdField;

export const getDatasetConfig = (
  state: AppState,
  datasetId: UniqueId
): DatasetConfig => getDatasetState(state, datasetId).config;

export const getDatasetConfigCurrentPreset = (
  state: AppState,
  datasetId: UniqueId
): IFrontDatasetPreset => {
  const { currentPreset, preset } = getDatasetConfig(state, datasetId);
  return preset[currentPreset];
};

export const getDatasetSelectedIds = (
  state: AppState,
  datasetId: UniqueId
): TDatabaseId[] => getDatasetState(state, datasetId).selectedIds;

export const filterDatasetsForCategory = createSelector(
  [
    (state: AppState) => state.dataset,
    (_state: AppState, categoryId: UniqueId) => categoryId,
  ],
  (datasetState, categoryId): Record<UniqueId, DatasetGridViewModel> => {
    const datasets: Record<UniqueId, DatasetGridViewModel> = {};
    for (const id of Object.keys(datasetState)) {
      const dataset = datasetState[id];
      if (dataset.categoryId === categoryId) datasets[id] = dataset;
    }
    return datasets;
  }
);

export const getFormDataset = (
  state: AppState,
  formId: UniqueId
): UniqueId | undefined => {
  const { source } = getFormState(state, formId);
  if (
    source.type === FormSourceType.PREDEFINED &&
    source.action !== PredefinedForms.REVIEW
  ) {
    return undefined;
  }

  return source.dataset;
};

export const getAllDatasetsIds = createSelector(
  [(state: AppState) => state.dataset],
  dataset => Object.keys(dataset)
);

export const getDatasetActiveRowId = (
  state: AppState,
  datasetId: UniqueId
): number | null => getDatasetState(state, datasetId).activeRowId;

export const isDatasetConfigFormShow = (
  state: AppState,
  datasetId: UniqueId
): boolean | undefined => getDatasetState(state, datasetId).config.showForm;

export const getDatasetTitle = (
  state: AppState,
  datasetId: UniqueId
): string => {
  const dataset = getDatasetState(state, datasetId);
  return dataset
    ? (dataset.header ??
        state.category[dataset.categoryId].category.datasets[datasetId].title)
    : '';
};

export const getDatasetName = (
  state: AppState,
  datasetId: UniqueId
): string => {
  const dataset = getDatasetState(state, datasetId);
  const categoryDataset =
    state.category[dataset.categoryId].category.datasets[datasetId];
  return categoryDataset.name;
};

export const getDatasetIdByName = createSelector(
  [
    (state: AppState, _datasetName: TDatasetName, categoryId: UniqueId) =>
      getCategoryState(state, categoryId).datasets,
    (_state: AppState, datasetName: TDatasetName) => datasetName,
  ],
  (datasets, datasetName): UniqueId =>
    (
      Object.values(datasets).find(({ name }) => name === datasetName) ||
      ({} as DatasetViewModel)
    ).id
);

export const getDatasetIdFields = createSelector(
  [
    (state: AppState, datasetId: UniqueId) =>
      getDatasetState(state, datasetId).rowIdField,
    (state: AppState, datasetId: UniqueId) =>
      getDatasetState(state, datasetId).treeField,
  ],
  (
    rowIdField,
    treeField
  ): Pick<DatasetGridViewModel, 'rowIdField' | 'treeField'> => ({
    rowIdField,
    treeField,
  })
);

export const isDatasetRoot = (state: AppState, dataset: UniqueId): boolean => {
  const categoryId = getDatasetCategoryId(state, dataset);
  const datasetId = getCategoryMasterDataset(state, categoryId);

  return datasetId.id === dataset;
};

export const getDatasetConditions = (
  state: AppState,
  datasetId: UniqueId
): TDatasetQueryParams => {
  const categoryId = getDatasetCategoryId(state, datasetId);
  const categoryState = getCategoryState(state, categoryId);
  const datasetName = getDatasetName(state, datasetId);

  return categoryState.source.displayModeConditions[datasetName];
};

export const getDatasetConditionsCount = createSelector(
  [
    (state: AppState, datasetId: UniqueId) => {
      const categoryId = getDatasetCategoryId(state, datasetId);
      return getCategoryState(state, categoryId).source;
    },
    (state: AppState, datasetId: UniqueId) =>
      getDatasetConditions(state, datasetId).conditions,
  ],
  (source, conditions): number | string => {
    if (source.inParams?.linksGraph?.mode === DocLinksMode.SINGLE) return 0;
    if (source.inParams?.linksGraph?.isIgnoreConditions) return '✖';
    return Object.keys(conditions).filter(val => !val.startsWith('$')).length;
  }
);

export const getDatasetFieldsConditionsCount = createSelector(
  [
    (state: AppState, datasetId: UniqueId) =>
      getDatasetConditions(state, datasetId).fieldsConditions,
  ],
  fieldsConditions => Object.keys(fieldsConditions).length
);

export const isDatasetOutdated = (
  state: AppState,
  datasetId: UniqueId
): boolean => getDatasetState(state, datasetId).outdated;

export const getDatasetEditableFields = (
  state: AppState,
  datasetId: UniqueId
): DatasetGridViewModel['editableFields'] =>
  getDatasetState(state, datasetId).editableFields;

export const getDatasetKeyMap = (
  state: AppState,
  datasetId: UniqueId
): CommandsNameKeyMap | undefined => getDatasetState(state, datasetId).keyMap;

export const getDatasetRecordViewMap = (
  state: AppState,
  datasetId: UniqueId
): TRecordViewMap | undefined =>
  getDatasetState(state, datasetId).recordViewMap;

export const getDatasetHideCheckbox = (
  state: AppState,
  datasetId: UniqueId
): boolean | undefined => getDatasetState(state, datasetId).hideCheckbox;

export const isTotalsInvalid = (
  state: AppState,
  datasetId: UniqueId
): boolean => !!state.dataset[datasetId].totalsInvalid;

export const getDatasetComputedTotals = createSelector(
  [
    (state: AppState, datasetId: UniqueId) =>
      getDatasetTotalsValues(state, datasetId),
    (state: AppState, datasetId: UniqueId) =>
      getDatasetConfigCurrentPreset(state, datasetId).totals,
    (state: AppState, datasetId: UniqueId) =>
      getDatasetSelectedIds(state, datasetId),
    (state: AppState, datasetId: UniqueId) => getDatasetRows(state, datasetId),
    (state: AppState, datasetId: UniqueId) =>
      getDatasetRowIdField(state, datasetId),
  ],
  (baseTotals, totalsConfig, selectedIds, rows, rowIdField) =>
    computeSelectedTotals(
      rows,
      selectedIds,
      rowIdField,
      baseTotals,
      totalsConfig
    )
);

export const getSecondaryTablesData = createSelector(
  [
    (state: AppState, datasetId: UniqueId) =>
      getAllChildrenDatasetsFromCategory(state, datasetId),
    (state: AppState) => state.dataset,
    (state: AppState) => state.category,
  ],
  (ids, datasetState, allCategoryState) =>
    ids.map(id => {
      const ds = datasetState[id];
      return {
        id,
        columns: ds.columns,
        config: ds.config,
        title:
          ds.header ??
          allCategoryState[ds.categoryId].category.datasets[id].title,
      };
    })
);

export const getDatasetHasConditionsHook = (
  state: AppState,
  datasetId: UniqueId
): boolean =>
  state.category[getDatasetCategoryId(state, datasetId)].category.source
    .displayModeHooks?.open === 'conditions';
